// apps/office/src/components/LabourPDF.jsx
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import pb from '../lib/pocketbaseClient';

// Brand assets
const LOGO_URL = 'https://housedesigns99.com/api/files/f5eolh49d789mqi/1n6c9z84m3xhprb/qudexa_logo_1_VjmXzmdx6O.png';
const WEBSITE = 'qudexaprojects.com';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '1px solid #ccc',
    paddingBottom: 10,
  },
  logo: {
    width: 50,
    height: 50,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  brandName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'right',
    fontSize: 8,
    color: '#999',
    borderTop: '1px solid #eee',
    paddingTop: 8,
  },
  vendorSection: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    border: '1px solid #e2e8f0',
  },
  vendorName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  vendorDetail: {
    fontSize: 10,
    marginTop: 2,
    color: '#475569',
  },
  vendorSummary: {
    fontSize: 10,
    marginTop: 4,
    color: '#0f172a',
    fontWeight: 'bold',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottom: '1px solid #1e293b',
    paddingVertical: 5,
    fontWeight: 'bold',
    backgroundColor: '#f1f5f9',
    paddingHorizontal: 5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #e2e8f0',
    paddingVertical: 5,
    paddingHorizontal: 5,
  },
  colName: { width: '22%' },
  colSite: { width: '20%' },
  colSkill: { width: '12%' },
  colAddress: { width: '26%' },
  colAadhar: { width: '20%' },
  cardPage: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  card: {
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardImage: {
    width: 80,
    height: 80,
    marginRight: 15,
    borderRadius: 4,
    objectFit: 'cover',
  },
  cardDetails: {
    flex: 1,
  },
  cardName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  cardAadhar: {
    fontSize: 10,
    color: '#475569',
    marginTop: 2,
  },
  cardAddress: {
    fontSize: 10,
    color: '#475569',
    marginTop: 2,
  },
  cardMeta: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 2,
  },
  cardSkill: {
    fontSize: 10,
    fontWeight: 'bold',
    marginTop: 2,
  },
});

// ─── Header Component ──────────────────────────────────────
const Header = () => (
  <View style={styles.header}>
    <View style={styles.brand}>
      <Image src={LOGO_URL} style={styles.logo} />
      <Text style={styles.brandName}>Qudexa Projects</Text>
    </View>
    <Text style={{ fontSize: 10, color: '#64748b' }}>Labour Management</Text>
  </View>
);

// ─── Footer Component ──────────────────────────────────────
const Footer = ({ date }) => (
  <View style={styles.footer}>
    <Text>{WEBSITE} • Generated: {date.toLocaleString()}</Text>
  </View>
);

// ─── Helper to compute labour stats ──────────────────────
const computeStats = (labours) => {
  const total = labours.length;
  const skilled = labours.filter(l => l.skill === 'skilled').length;
  const unskilled = total - skilled;
  return { total, skilled, unskilled };
};

// ─── List Format PDF ──────────────────────────────────────
export const LabourListPDF = ({ vendor, labours, generatedDate }) => {
  const stats = computeStats(labours);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header />
        <View style={styles.vendorSection}>
          <Text style={styles.vendorName}>Vendor: {vendor.name}</Text>
          <Text style={styles.vendorDetail}>Phone: {vendor.phone || 'N/A'} • Email: {vendor.email || 'N/A'}</Text>
          <Text style={styles.vendorDetail}>Address: {vendor.address || 'N/A'}</Text>
          <Text style={styles.vendorSummary}>
            Total Labour: {stats.total}  |  Skilled: {stats.skilled}  |  Unskilled: {stats.unskilled}
          </Text>
          <Text style={{ marginTop: 4, fontSize: 9, color: '#64748b' }}>
            Generated on: {generatedDate.toLocaleString()}
          </Text>
        </View>

        <View style={styles.tableHeader}>
          <Text style={styles.colName}>Name</Text>
          <Text style={styles.colSite}>Site</Text>
          <Text style={styles.colSkill}>Skill</Text>
          <Text style={styles.colAddress}>Address</Text>
          <Text style={styles.colAadhar}>Aadhar</Text>
        </View>
        {labours.map((lab, idx) => (
          <View key={idx} style={styles.tableRow}>
            <Text style={styles.colName}>{lab.name}</Text>
            <Text style={styles.colSite}>{lab.expand?.site?.name || '—'}</Text>
            <Text style={styles.colSkill}>{lab.skill || '—'}</Text>
            <Text style={styles.colAddress}>{lab.address || '—'}</Text>
            <Text style={styles.colAadhar}>{lab.aadhar}</Text>
          </View>
        ))}
        <Footer date={generatedDate} />
      </Page>
    </Document>
  );
};

// ─── Cards Format PDF ──────────────────────────────────────
export const LabourCardsPDF = ({ vendor, labours }) => {
  const stats = computeStats(labours);
  return (
    <Document>
      {labours.map((lab, idx) => (
        <Page key={idx} size="A4" style={styles.cardPage}>
          <Header />
          <View style={styles.vendorSection}>
            <Text style={styles.vendorName}>Vendor: {vendor.name}</Text>
            <Text style={styles.vendorDetail}>Phone: {vendor.phone || 'N/A'} • Email: {vendor.email || 'N/A'}</Text>
            <Text style={styles.vendorDetail}>Address: {vendor.address || 'N/A'}</Text>
            <Text style={styles.vendorSummary}>
              Total Labour: {stats.total}  |  Skilled: {stats.skilled}  |  Unskilled: {stats.unskilled}
            </Text>
          </View>

          <View style={styles.card}>
            {lab.photo ? (
              <Image
                src={pb.files.getUrl(lab, lab.photo)}
                style={styles.cardImage}
              />
            ) : (
              <View style={[styles.cardImage, { backgroundColor: '#e2e8f0', justifyContent: 'center', alignItems: 'center' }]}>
                <Text style={{ fontSize: 8, color: '#94a3b8' }}>No Photo</Text>
              </View>
            )}
            <View style={styles.cardDetails}>
              <Text style={styles.cardName}>{lab.name}</Text>
              <Text style={styles.cardAadhar}>Aadhar: {lab.aadhar}</Text>
              <Text style={styles.cardAddress}>Address: {lab.address || '—'}</Text>
              <Text style={styles.cardMeta}>Site: {lab.expand?.site?.name || '—'}</Text>
              <Text style={styles.cardSkill}>
                Skill: {lab.skill === 'skilled' ? '✅ Skilled' : '🟡 Unskilled'}
              </Text>
              <Text style={styles.cardMeta}>Status: {lab.status || 'active'}</Text>
            </View>
          </View>

          <Footer date={new Date()} />
        </Page>
      ))}
    </Document>
  );
};